// evo/rooms/EvoMyPriceBadge.tsx
// MPH-935: the my-price pill for an Evo room card, shown when the room's rate is quoted for the
// member (`isMyPrice`) — plus the discount that rate carries, where there is one worth showing.
//
// The Evo room card had no my-price treatment at all: the badge list knew nothing about it and the
// rate rendered as an ordinary one, so a member price looked identical to a public one. Legacy shows
// two things for it — the price recoloured in the my-price teal (RoomPrice's $isMyPrice) and a `-N%`
// chip (MyPriceDiscountPercentage). The Evo room card shows a price *difference* rather than an
// absolute price, so recolouring that would say the wrong thing; both signals go in the badge row
// instead, pairing label with percentage the way the Evo results card does (MyPriceBadge).
import styled from 'styled-components';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { MIN_MY_PRICE_DISCOUNT_PERCENTAGE } from '@/app/components/hotel-detail/rooms/RoomPrice';

const MyPricePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  padding: ${({ theme }) => theme.kallisto.space.space50} ${({ theme }) => theme.kallisto.space.space100};
  background-color: ${({ theme }) => theme.kallisto.color.common.special.myPrice[500]};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
`;

const DiscountPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.kallisto.space.space50} ${({ theme }) => theme.kallisto.space.space100};
  background-color: ${({ theme }) => theme.kallisto.color.common.special.myPrice[100]};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
`;

// The label markup carries its own emphasis; keep it on one line inside the pill.
const PillLabel = styled(Text)`
  white-space: nowrap;
`;

const DiscountText = styled(Text)`
  color: ${({ theme }) => theme.kallisto.color.common.special.myPrice[700]};
`;

const PillIcon = styled.img`
  width: 16px;
  height: 16px;
`;

export function EvoMyPriceBadge({ discountPercentage }: { discountPercentage?: number }) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;
  // The same floor legacy applies, taken from legacy's own chip rather than restated: below it the
  // saving is not worth a badge.
  const showDiscount = !!discountPercentage && discountPercentage >= MIN_MY_PRICE_DISCOUNT_PERCENTAGE;

  return (
    <>
      <MyPricePill>
        <PillIcon src={`${imageBasePath}icons/my_price_white.svg`} alt="" />
        {/* The same key and markup the Evo results card's badge uses, so the two read identically. */}
        <PillLabel
          variant="BodySmall"
          weight="bold"
          color="textOnDark"
          dangerouslySetInnerHTML={{ __html: t('vacation.oss.cards.myprice_label_html') }}
        />
      </MyPricePill>
      {showDiscount && (
        <DiscountPill>
          <DiscountText variant="BodySmall" weight="bold">{`-${discountPercentage}%`}</DiscountText>
        </DiscountPill>
      )}
    </>
  );
}
